/**
 * WizardProfessionalsScreen Component (Web Version)
 * Per-service professional assignment using v2 API format
 * Features: Service cards with avatar selection, index-based assignments
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { getAvailableProfessionals } from '../../services/wizardApiService';

const WizardProfessionalsScreen = () => {
  const [imageErrors, setImageErrors] = useState({});
  const buttonShakeRef = useRef(null);

  const {
    selectedServices,
    selectedDate,
    availableProfessionals,
    serviceAssignments,
    isLoading,
    error,
    setAvailableProfessionals,
    setServiceAssignment,
    setLoading,
    setError,
    clearError,
    goToNextStep,
    hasAllServiceAssignments,
  } = useWizardStore();

  // Load professionals when component mounts or date changes
  useEffect(() => {
    loadProfessionals();
  }, [selectedDate]);

  const loadProfessionals = useCallback(async () => {
    if (!selectedDate || selectedServices.length === 0) return;

    setLoading(true);
    clearError();

    try {
      // Use originalServiceId for cart items with variations
      const serviceIds = selectedServices.map(service => service.originalServiceId || service.id);
      // Deduplicate service IDs (same service with different variations)
      const uniqueServiceIds = [...new Set(serviceIds)];
      const professionals = await getAvailableProfessionals(uniqueServiceIds, selectedDate);
      setAvailableProfessionals(professionals);

      // Auto-assign if only one professional can do a service
      selectedServices.forEach((service, index) => {
        const serviceId = service.originalServiceId || service.id;
        const capablePros = professionals.filter(prof =>
          prof.services_offered?.includes(serviceId)
        );
        if (capablePros.length === 1) {
          setServiceAssignment(index, capablePros[0].id);
        }
      });
    } catch (err) {
      setError('Erro ao carregar profissionais. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedServices, setLoading, clearError, setAvailableProfessionals, setError, setServiceAssignment]);

  // Get professionals who can do a specific service
  // Use originalServiceId for cart items with variations
  const getProfessionalsForService = (service) => {
    const serviceId = service.originalServiceId || service.id;
    return availableProfessionals.filter(prof =>
      prof.services_offered?.includes(serviceId)
    );
  };

  // Handle professional selection for a service
  const handleProfessionalSelect = (serviceIndex, professionalId) => {
    setServiceAssignment(serviceIndex, professionalId);
  };

  // Check if a professional is selected for a service
  const isProfessionalSelected = (serviceIndex, professionalId) => {
    return serviceAssignments[String(serviceIndex)] === professionalId;
  };

  // Handle image errors
  const handleImageError = (professionalId) => {
    setImageErrors(prev => ({ ...prev, [professionalId]: true }));
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return '-';
    return `R$ ${Number(price).toFixed(2)}`;
  };

  // Get display name for service with variation
  const getServiceDisplayName = (service) => {
    const variation = service.selectedVariation;
    if (variation && variation.name && variation.name !== 'Padrão') {
      return `${service.name} - ${variation.name}`;
    }
    return service.name;
  };

  // Render professional avatar
  const renderProfessionalAvatar = (professional, size = 48) => {
    const hasError = imageErrors[professional.id];
    const photoUrl = professional.photo_url || professional.photo_path;
    const hasValidPhoto = photoUrl && !hasError;

    if (!hasValidPhoto) {
      const initials = (professional.full_name || professional.email || 'U').charAt(0).toUpperCase();
      return (
        <div
          className="rounded-full bg-brand-dark/10 dark:bg-brand-light/10 flex items-center justify-center text-brand-dark dark:text-brand-light font-medium"
          style={{ width: size, height: size, fontSize: size * 0.4 }}
        >
          {initials}
        </div>
      );
    }

    return (
      <img
        src={photoUrl}
        alt={professional.full_name || professional.email}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
        onError={() => handleImageError(professional.id)}
      />
    );
  };

  // Render professional selection chip
  const renderProfessionalChip = (professional, serviceIndex, isSelected) => {
    return (
      <button
        key={professional.id}
        onClick={() => handleProfessionalSelect(serviceIndex, professional.id)}
        className={`flex flex-col items-center p-3 min-w-[80px] transition-all duration-200 ${
          isSelected
            ? 'bg-brand-dark dark:bg-brand-light border-2 border-brand-dark dark:border-brand-light'
            : 'bg-transparent border border-brand-dark/20 dark:border-brand-light/20 hover:border-brand-dark/40 dark:hover:border-brand-light/40'
        }`}
      >
        {/* Avatar */}
        <div className="relative mb-2">
          {renderProfessionalAvatar(professional, 48)}
          {isSelected && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-600 dark:bg-green-400 rounded-full flex items-center justify-center">
              <Check size={12} className="text-white dark:text-black" strokeWidth={3} />
            </div>
          )}
        </div>

        {/* Name */}
        <span className={`text-xs font-medium text-center leading-tight truncate w-full ${
          isSelected
            ? 'text-brand-light dark:text-brand-dark'
            : 'text-brand-dark dark:text-brand-light'
        }`}>
          {(professional.full_name || professional.email || '').split(' ')[0]}
        </span>
      </button>
    );
  };

  // Render service card with professional selection
  const renderServiceCard = (service, index) => {
    const capableProfessionals = getProfessionalsForService(service);
    const assignedProfessionalId = serviceAssignments[String(index)];
    const hasAssignment = !!assignedProfessionalId;

    return (
      <div
        key={service.cartItemId || service.id || index}
        className={`border ${
          hasAssignment
            ? 'border-green-600/30 dark:border-green-400/30'
            : 'border-brand-dark/10 dark:border-brand-light/10'
        } bg-brand-light dark:bg-brand-dark mb-4`}
      >
        {/* Service Header */}
        <div className={`p-4 border-b ${
          hasAssignment
            ? 'border-green-600/20 dark:border-green-400/20 bg-green-600/5 dark:bg-green-400/5'
            : 'border-brand-dark/10 dark:border-brand-light/10'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-base font-medium text-brand-dark dark:text-brand-light tracking-wide">
                {service.name}
              </h3>
              {service.selectedVariation?.name && service.selectedVariation.name !== 'Padrão' && (
                <span className="inline-block text-xs bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark dark:text-brand-light border border-brand-dark/30 dark:border-brand-light/30 px-2 py-0.5 mt-1 tracking-wide">
                  {service.selectedVariation.name}
                </span>
              )}
              <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 mt-1 tracking-wide">
                {formatDuration(service.duration_minutes)} • {formatPrice(service.price)}
              </p>
            </div>
            {hasAssignment && (
              <div className="w-6 h-6 bg-green-600 dark:bg-green-400 flex items-center justify-center">
                <Check size={14} className="text-white dark:text-black" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>

        {/* Professional Selection */}
        <div className="p-4">
          <p className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-3">
            Escolha o profissional:
          </p>

          {capableProfessionals.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {capableProfessionals.map(professional =>
                renderProfessionalChip(
                  professional,
                  index,
                  isProfessionalSelected(index, professional.id)
                )
              )}
            </div>
          ) : (
            <p className="text-sm text-brand-dark/40 dark:text-brand-light/40 italic">
              Nenhum profissional disponível para este serviço
            </p>
          )}
        </div>
      </div>
    );
  };

  // Render progress indicator
  const renderProgressIndicator = () => {
    const assignedCount = Object.keys(serviceAssignments).length;
    const totalCount = selectedServices.length;

    return (
      <div className="sticky top-0 z-10 bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-brand-dark dark:text-brand-light tracking-wide">
            Atribuir profissionais
          </p>
          <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
            {assignedCount} de {totalCount} serviço{totalCount > 1 ? 's' : ''} atribuído{assignedCount > 1 ? 's' : ''}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-1 bg-brand-dark/10 dark:bg-brand-light/10">
          <div
            className="h-full bg-green-600 dark:bg-green-400 transition-all duration-300"
            style={{ width: `${(assignedCount / totalCount) * 100}%` }}
          />
        </div>
      </div>
    );
  };

  // Render loading state
  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent mx-auto mb-4 animate-spin rounded-full"></div>
      <p className="text-brand-dark/60 dark:text-brand-light/60 text-xs uppercase tracking-wider">Carregando profissionais...</p>
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div className="text-center py-16 px-4">
      <p className="text-red-600 dark:text-red-400 mb-6 tracking-wide">{error}</p>
      <button
        onClick={loadProfessionals}
        className="px-6 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
      >
        Tentar novamente
      </button>
    </div>
  );

  // Check if can continue
  const canContinue = hasAllServiceAssignments();

  // Get button text
  const getButtonText = () => {
    if (canContinue) {
      return 'Continuar';
    }

    const unassignedServices = selectedServices.filter((_, index) =>
      !serviceAssignments[String(index)]
    );

    if (unassignedServices.length > 0) {
      return `Selecione profissional para: ${unassignedServices[0].name}`;
    }

    return 'Selecione os profissionais';
  };

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Progress Indicator */}
      {renderProgressIndicator()}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto pb-48 mobile-scroll">
        <div className="p-4">
          {isLoading ? (
            renderLoadingState()
          ) : error ? (
            renderErrorState()
          ) : (
            selectedServices.map((service, index) =>
              renderServiceCard(service, index)
            )
          )}
        </div>
      </div>

      {/* Continue Button */}
      <div className="fixed bottom-16 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 p-6">
        <button
          ref={buttonShakeRef}
          onClick={() => {
            if (canContinue) {
              goToNextStep();
            }
          }}
          disabled={!canContinue}
          className={`w-full py-5 border font-medium uppercase tracking-widest text-sm transition-all duration-300 ${
            canContinue
              ? 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90'
              : 'bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark/40 dark:text-brand-light/40 border-brand-dark/10 dark:border-brand-light/10 cursor-not-allowed'
          }`}
        >
          {getButtonText()}
        </button>
      </div>

      {/* Custom Styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default WizardProfessionalsScreen;
